import React, { useState, useEffect, useCallback, useRef } from "react";
import CasinoHeader from "./casino-header";
import CasinoGameGrid from "./casino-game-grid";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { favoriteGameList } from "../redux/slices/games/gameFavorite/favoriteGameListSlice";
import { getDreamCasinoGames } from "../redux/slices/games/casino/dreamCasinoGameListSlice";
import { getTournamentGames } from "../redux/slices/tournaments/tournamentPlaySlice";
import { claimPromotion } from "../redux/slices/promotions/promotionClaimSlice";

const CasinoComponentTournament = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const { pathname, state: locationState } = useLocation();
  const configData = useSelector((state) => state.config.data);

  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [visibleCount, setVisibleCount] = useState(10);
  const [gameList, setGameList] = useState([]);
  const [favSelect, setFavSelect] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategoryDropdown, setSelectedCategoryDropdown] = useState({
    label: "",
    value: "",
  });
  const [selectedProvider, setSelectedProvider] = useState([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [options, setOptions] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [totalGames, setTotalGames] = useState(0);

  const hasMoreRef = useRef(true);
  const loadingRef = useRef(false);
  const { data: profileData } = useSelector((state) => state.getProfile);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  /** ---------------- HANDLERS ----------------- **/
  const handleCategoryChange = (cat) => {
    setPage(1);
    setLoading(true);
    setGameList([]);
    setSelectedCategory(cat);
    setVisibleCount(10);
    setFavSelect(false);

    if (cat == "favourite") {
      setFavSelect(true);
      setSelectedProvider([]);
    }
  };

  const handleSearch = useCallback((val) => {
    setSearchValue(val);
    setSelectedProvider([]);
    setSelectedCategoryDropdown({ label: "", value: "" });
  }, []);

  const handleClear = () => {
    setSearchValue("");
    setVisibleCount(10);
  };

  const handleShowMore = () => {
    setVisibleCount((prev) => Math.min(prev + 10, totalGames));
    if (hasMore && !loading) {
      setPage((prev) => prev + 1);
    }
  };

  const handleProviderChange = (option) => {
    setSelectedProvider(option);
  };

  const handleCategoryDropdownChange = (option) => {
    setSelectedCategoryDropdown(option);
    setFavSelect(false);
    if (option.value == "favourite") {
      setSelectedProvider([]);
      setFavSelect(true);
    }
  };

  const handleResize = () => {
    setIsMobile(window.innerWidth < 1024);
  };

  const handleScroll = () => {
    if (!isMobile) return;
    if (!hasMoreRef.current || loadingRef.current) return;
    const threshold = 1500;
    if (
      window.innerHeight + document.documentElement.scrollTop >=
      document.documentElement.scrollHeight - threshold
    ) {
      setPage((prev) => prev + 1);
    }
  };

  /** ---------------- API HANDLER ----------------- **/
  const handleGameList = () => {
    let payload = {
      page: page || 1,
      limit: 10,
      perpage: 20,
      search_by_category: "",
      search_by_provider: "",
      search_by_text: searchValue,
      is_popular: 0,
      parent_user_id: profileData?.user?.parent_user_id,
    };

    if (params?.tournamentID || params?.promotionId) {
      if (isMobile) {
        payload.search_by_category =
          selectedCategoryDropdown?.value || locationState?.name || "";
        payload.search_by_provider = selectedProvider?.originalValue || "";
      } else {
        payload.search_by_category =
          selectedCategory || locationState?.name || "";
        payload.search_by_provider = selectedProvider?.originalValue || "";
      }
      if (params?.promotionId) {
        payload.promotion_id = params?.promotionId;
      } else {
        payload.tournament_id = params?.tournamentID;
      }
    }

    setLoading(true);
    const api = params?.tournamentID
      ? getTournamentGames
      : params?.promotionId
      ? claimPromotion
      : getDreamCasinoGames;

    dispatch(api(payload))
      .unwrap()
      .then((res) => {
        setLoading(false);
        if (page == 1) {
          setGameList(
            params?.tournamentID || params?.promotionId
              ? res?.data?.games
              : res.data
          );
        } else {
          setGameList((prev) => [
            ...prev,
            ...(params?.tournamentID || params?.promotionId
              ? res?.data?.games
              : res.data),
          ]);
        }
        if (params?.tournamentID || params?.promotionId) {
          const providerOptions = [
            { value: "", originalValue: "", label: "All Providers" },
            ...(res?.data?.provider_stats?.map((i) => ({
              value: i._id,
              originalValue: i.provider_name,
              label: `${i.provider_name}-${i.sub_provider_name}`,
            })) || []),
          ];

          const categoryData = res?.data?.category_stats?.map((i) => ({
            value: i.category,
            label: i.category,
          }));
          setOptions(providerOptions || []);
          setCategories(categoryData || []);
          setCategoryOptions(categoryData || []);
          setTotalGames(res.total || 0);
        } else {
          setTotalGames(res.totalCount || 0);
        }

        setHasMore(res.has_more);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Failed :", error);
      });
  };

  /** ---------------- EFFECTS ----------------- **/

  useEffect(() => {
    if ((favSelect && !params?.tournamentID) || !params?.promotionId) {
      const payload = { page: 1, perPage: 10, search: "" };
      dispatch(favoriteGameList(payload))
        .unwrap()
        .then((res) => {
          setGameList(res.data);
          setTotalGames(res.totalCount || res.pagination.total);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Failed :", error);
        });
    }
  }, [favSelect, params?.tournamentID, params?.promotionId]);

  useEffect(() => {
    if (params?.tournamentID || params?.promotionId) return;

    if (configData) {
      setSelectedCategory("");
      const providerOptions = [
        { value: "", originalValue: "", label: "All Providers" },
        ...(configData.casinoProviders?.map((i) => ({
          value: i._id,
          originalValue: i.name,
          label: `${i.name}-${i.sub_provider}`,
        })) || []),
      ];

      setPage(1);
      setOptions(providerOptions);

      if (locationState?.sidebar_cat == "casino") {
        const configCat = configData.casinoCategories?.map((i) => ({
          value: i.name,
          label: i.name,
        }));
        configCat.unshift({ value: "favourite", label: "Favourite" });
        setCategories(configCat);
        setCategoryOptions(configCat);
      } else if (locationState?.sidebar_cat == "live") {
        const liveCats = configData.liveCasinoCategory.map((i) => ({
          value: i.name,
          label: i.name,
        }));
        liveCats.unshift({ value: "favourite", label: "Favourite" });
        setCategories(liveCats);
        setCategoryOptions(liveCats);
      }
    }
  }, [configData, locationState, params?.tournamentID, params?.promotionId]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobile]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathname]);

  useEffect(() => {
    if (params?.tournamentID || params?.promotionId) {
      setGameList([]);
      setLoading(true);
      setPage(1);
      handleGameList();
    } else if (locationState && !isMobile) {
      setGameList([]);
      setLoading(true);
      setPage(1);
      handleGameList();
    }
  }, [
    params?.tournamentID,
    locationState,
    selectedCategory,
    selectedProvider,
    searchValue,
    params?.promotionId,
  ]);

  const isFirstPageLoad = useRef(true);

  useEffect(() => {
    if (page) {
      if (isFirstPageLoad.current) {
        isFirstPageLoad.current = false;
        return;
      }
      handleGameList();
    }
  }, [page]);

  /** ---------------- RENDER ----------------- **/
  const categoryProps = {
    selectedCategory,
    onCategoryChange: handleCategoryChange,
    categories,
    isMobile,
  };

  const searchProps = {
    searchValue,
    onSearch: handleSearch,
    onClear: handleClear,
  };

  const dropdownProps = {
    selectedProvider,
    handleProviderChange,
    selectedCategoryDropdown,
    handleCategoryDropdownChange,
    categoryOptions,
    options,
  };

  return (
    <main className="casino-main">
      <CasinoHeader
        categoryProps={categoryProps}
        searchProps={searchProps}
        dropdownProps={dropdownProps}
      />
      <div className="content">
        <CasinoGameGrid
          games={gameList}
          favSelect={favSelect}
          totalGames={totalGames}
          onShowMore={handleShowMore}
          onFavClick={() => {}}
          loading={loading}
          isMobile={isMobile}
          params={params}
        />
      </div>
    </main>
  );
};

export default CasinoComponentTournament;
